import {
	ANSWER_SOURCE,
	getDisplayableQuestions,
	getValidationResult,
	TQuestionWithAnswer,
	TEDCApplicant,
	VALIDATION_STATUS
} from "@smvtech/x-flux";
import { Check, Spinner, UploadSimple } from "@phosphor-icons/react";
import { useDocumentItem } from "pages/EDCFlow/hooks/useDocumentItem";
import DocumentPreview from "pages/EDCFlow/components/VisaForm/DocumentsList/DocumentPreview";
import DocumentUpload from "pages/EDCFlow/components/VisaForm/DocumentsList/DocumentUpload";
import UploadingProgress from "./UploadingProgress";
import { useMemo } from "react";

type CompactCardProps = {
	document: TQuestionWithAnswer;
	activeApplicantData: TEDCApplicant;
};

const CompactDocumentCard = ({ document, activeApplicantData }: CompactCardProps) => {
	const questions = useMemo(
		() => getDisplayableQuestions(activeApplicantData.questions, [ANSWER_SOURCE.PASSPORT_FRONT_OCR]),
		[activeApplicantData.questions]
	);

	const validationResult = useMemo(() => getValidationResult(document), [document]);
	const isUnfixable = validationResult?.status === VALIDATION_STATUS.UNFIXABLE;
	const isVerified =
		validationResult?.status === VALIDATION_STATUS.PERFECT ||
		validationResult?.status === VALIDATION_STATUS.FIXABLE;

	const {
		signedUrl,
		isLoading,
		uploading,
		uploadFailed,
		hasDocument,
		isPdf,
		reUploadInputId,
		handleUploadStart,
		handleUploadProgress,
		handleUploadComplete,
		handleReUpload,
		handlePreviewClick
	} = useDocumentItem({ document, activeApplicantData, questions, validationResult });

	return (
		<div className={`flex gap-3 bg-white rounded-xl border p-3 ${isUnfixable ? "border-red-200" : "border-[rgba(0,0,0,0.08)]"}`}>
			<div className={`w-16 h-16 shrink-0 rounded-lg border border-dashed flex items-center justify-center overflow-hidden ${isUnfixable ? "border-red-400 bg-red-50" : "border-indigo-600 bg-[#F8F7FD]"}`}>
				{hasDocument ? (
					<DocumentPreview
						signedUrl={signedUrl!}
						isPdf={isPdf}
						alt={document.question.category}
						onPreviewClick={handlePreviewClick}
						isLoading={isLoading}
					/>
				) : uploading.isUploading ? (
					<Spinner size={18} className="animate-spin text-indigo-600" />
				) : (
					<DocumentUpload
						document={document}
						activeApplicantData={activeApplicantData}
						onUploadStart={handleUploadStart}
						onUploadProgress={handleUploadProgress}
						onUploadComplete={handleUploadComplete}
					/>
				)}
			</div>

			<div className="flex flex-col flex-1 justify-center gap-1.5 min-w-0">
				{isVerified && (
					<span className="flex items-center gap-1 w-fit text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
						<Check size={11} weight="bold" />
						Verified
					</span>
				)}

				<p className="text-sm font-medium text-gray-900 truncate">{document.question.category}</p>

				<input
					type="file"
					accept="image/jpeg,image/png,application/pdf"
					hidden
					id={reUploadInputId}
					onChange={(e) => handleReUpload(e.target.files)}
				/>

				{uploading.isUploading ? (
					<UploadingProgress />
				) : isUnfixable ? (
					<>
						<label
							htmlFor={reUploadInputId}
							className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-b from-[#E5483B] to-[#B42318] text-white cursor-pointer">
							<UploadSimple size={13} />
							Re-Upload
						</label>
						{uploadFailed && (
							<p className="text-xs text-red-600">Upload failed. Please try again.</p>
						)}
					</>
				) : null}
			</div>
		</div>
	);
};

type Props = {
	documents: TQuestionWithAnswer[];
	activeApplicantData: TEDCApplicant;
};

const ErrorReviewDrawer = ({ documents, activeApplicantData }: Props) => {
	return (
		<div className="pb-6">
			<h2 className="text-lg font-semibold text-gray-900">
				Review {documents.length} Issue{documents.length !== 1 && "s"}
			</h2>

			<p className="text-sm text-gray-500 mt-1 mb-5">
				Please fix these errors to proceed with your application.
			</p>

			<div className="flex flex-col gap-3">
				{documents.map((doc) => (
					<CompactDocumentCard
						key={doc.id}
						document={doc}
						activeApplicantData={activeApplicantData}
					/>
				))}
			</div>
		</div>
	);
};

export default ErrorReviewDrawer;
